import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { LabApp } from "./design-lab/LabApp";
import { PlayPage } from "./play/PlayPage";
import { SellerCabinet } from "./seller/SellerCabinet";
import { AdminCabinet, TotpPage } from "./admin/AdminCabinet";
import { FamilyPage } from "./family/FamilyPage";
import { Paywall } from "./family/Paywall";
import { RewardsPage } from "./family/RewardsPage";
import { api } from "./api";
import { TRAINER_META } from "../content/catalog";
import { CHILD_STATUS, type MasteryStatus } from "../domain/mastery";
import { TutorLanding } from "./landing";
import { safeNext, useMe, type Me } from "./me";
import { PocketFrame, trainerEmoji } from "./shell";

const APEX = "https://clever.party1207.online";

type PublicPrice = {
  offerVersionId: string;
  accessProductId: string;
  kind: string;
  coverageKind: string;
  trainerSlug: string | null;
  title: string;
  priceKopecks: string;
};

function tenantSlugFromHost(): string | null {
  const host = window.location.hostname;
  if (host.endsWith(".clever.party1207.online") && host !== "clever.party1207.online") {
    return host.slice(0, -".clever.party1207.online".length);
  }
  return null;
}

function formatRub(kopecks: string | number): string {
  return `${(Number(kopecks) / 100).toLocaleString("ru-RU")} ₽`;
}

function Top({ me }: { me: Me }) {
  return (
    <header className="topbar">
      <a href="/" className="brand">Clever</a>
      <nav className="row">
        <a href="/families">Семьям</a>
        {me ? <a href="/app">Кабинет</a> : <a href="/login">Войти</a>}
      </nav>
    </header>
  );
}

function Landing() {
  const [me] = useMe();
  return (
    <PocketFrame>
      <Top me={me} />
      <TutorLanding />
    </PocketFrame>
  );
}

function Families() {
  const [me] = useMe();
  return (
    <PocketFrame>
      <Top me={me} />
      <main className="page">
        <h1>Семьям</h1>
        <p>Один аккаунт на семью, отдельный профиль на каждого ребёнка. Внутри — занятия, прогресс и домашние награды.</p>
        <p>Аккаунт — это вход, профиль — это ребёнок. Доступ, прогресс и награды закреплены за профилем, а не за почтой взрослого.</p>
        <div className="row">
          <a className="btn" href="/register">Создать аккаунт</a>
          <a className="btn ghost" href="/login">У меня уже есть аккаунт</a>
        </div>

        <h2>Профили детей</h2>
        <p>Для занятий хватит имени или псевдонима — фамилия не нужна.</p>
        <p>На общем устройстве ребёнок сам выбирает свой профиль, а разделы взрослого закрыты кодом: покупки, награды и настройки.</p>
        <p>Если у ребёнка свой аккаунт, он входит отдельно, а связь с семьёй подтверждается кодом-приглашением.</p>

        <h2>Пробный доступ</h2>
        <p>Семь дней с первого запуска занятия. Пробный период считается для конкретного ребёнка и для того, что вы открыли: одного тренажёра или всего каталога.</p>
        <p>Когда пробный период закончится, прогресс останется на месте.</p>
        <p>Пока идёт beta, оплата тестовая: деньги не списываются.</p>

        <h2>Прогресс без школьных оценок</h2>
        <p>Статус меняется не от одной удачной попытки, а от самостоятельных ответов и повторов в разные дни.</p>
        <ul>
          <li>Не начато — заданий ещё не было.</li>
          <li>Знакомимся — пройдено первое объяснение.</li>
          <li>Учусь — навык пока требует подсказок.</li>
          <li>Почти умею — получается самостоятельно, но нужна отложенная проверка.</li>
          <li>Закреплено — навык подтверждён без подсказок в разные дни.</li>
          <li>Пора повторить — доступно короткое повторение.</li>
        </ul>

        <h2>Домашние награды</h2>
        <p>Вы сами договариваетесь с ребёнком: за какой учебный результат — сколько рублей, Robux или своих «звёзд».</p>
        <p>Clever только считает. Награду выдаёт взрослый — ничего не переводится автоматически.</p>

        <h2>Кто видит данные ребёнка</h2>
        <p>Связанные взрослые видят прогресс, доступ и домашние награды.</p>
        <p>Репетитор — только обезличенную статистику: ни имени ребёнка, ни почты семьи, ни отдельных ответов, ни истории занятий, ни наград, ни голоса.</p>
        <p>Запись голоса живёт только в текущем упражнении и только в этом браузере: она не уходит на сервер и не оценивается автоматически.</p>
      </main>
    </PocketFrame>
  );
}

function Credits() {
  const [me] = useMe();
  return (
    <PocketFrame>
      <Top me={me} />
      <main className="page">
        <p className="badge">Credits</p>
        <h1>Лицензии и атрибуция</h1>
        <p>Сейчас в занятиях стоят наши собственные заглушки, а не записи носителя языка и не финальные иллюстрации. Лингвистическую и содержательную проверку ещё не проводили — поля reviewer и reviewed_at пустые намеренно.</p>

        <h2>Аудио</h2>
        <p>Оригинальный PCM-тон, сгенерированный для Clever. Лицензия CC0 1.0. Не скопирован из словаря, коммерческого курса или с публичного веба. Это не запись носителя Standard Southern British.</p>
        <p><a href="https://creativecommons.org/publicdomain/zero/1.0/">CC0 1.0</a></p>

        <h2>Изображения</h2>
        <p>Геометрические заглушки нарисованы для Clever. Лицензия project-original-placeholder. Это не сторонние иллюстрации из интернета. Предметные картинки для продакшена ещё нужно заказать отдельно.</p>

        <h2>Twemoji</h2>
        <p>Планируемые маскоты: Twemoji 17.0.3 by jdecked and contributors, лицензия CC BY 4.0. Текущие файлы — свои заглушки; SVG Twemoji в эту beta не подключены.</p>
        <p><a href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a></p>

        <h2>Правила</h2>
        <ul>
          <li>Во время занятия не работает никакой ИИ.</li>
          <li>Речь не распознаётся, произношение не оценивается автоматически.</li>
          <li>Пустые reviewer и reviewed_at значат, что проверку ещё не проводили.</li>
        </ul>
        <p><a href="/content/LICENCES.md">Заметки content-pack</a> · <a href="/legal/terms">Условия использования</a> · <a href="/legal/privacy">Политика конфиденциальности</a></p>
      </main>
    </PocketFrame>
  );
}

function TelegramLogin({ username, onAuth }: { username: string; onAuth: (payload: Record<string, unknown>) => void }) {
  useEffect(() => {
    const w = window as Window & { onTelegramAuth?: (user: Record<string, unknown>) => void };
    w.onTelegramAuth = onAuth;
    const host = document.getElementById("clever-telegram-login");
    if (!host) return;
    host.replaceChildren();
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", username);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");
    host.appendChild(script);
    return () => {
      delete w.onTelegramAuth;
      host.replaceChildren();
    };
  }, [username, onAuth]);
  return (
    <div>
      <p>Продолжить через Telegram</p>
      <div id="clever-telegram-login" />
    </div>
  );
}

function AuthForm({ mode }: { mode: "login" | "register" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const next = useMemo(() => safeNext(new URLSearchParams(loc.search).get("next")), [loc.search]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [err, setErr] = useState("");
  const [caps, setCaps] = useState<{ magicLink: boolean; telegram: boolean; telegramBotUsername: string | null }>({
    magicLink: false,
    telegram: false,
    telegramBotUsername: null,
  });
  useEffect(() => {
    api<{ magicLink: boolean; telegram: boolean; telegramBotUsername: string | null }>("/api/v1/auth/capabilities")
      .then(setCaps)
      .catch(() => undefined);
  }, []);
  return (
    <PocketFrame>
      <Top me={null} />
      <main className="page auth">
        <div className="auth-copy">
          <h1>{mode === "login" ? "Войти в Clever" : "Создать аккаунт"}</h1>
          <p className="lead">Выберите удобный способ входа.</p>
        </div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              if (mode === "register") {
                const res = await api<{ recoveryCodes: string[] }>("/api/v1/auth/register", {
                  method: "POST",
                  body: JSON.stringify({ email, password, accept }),
                });
                nav("/auth/recovery", { state: { recoveryCodes: res.recoveryCodes, next } });
                return;
              }
              await api("/api/v1/auth/password/login", { method: "POST", body: JSON.stringify({ email, password }) });
              nav(next);
            } catch (ex) {
              setErr((ex as Error).message);
            }
          }}
        >
          <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
          <label className="field">Пароль<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} /></label>
          {mode === "register" ? (
            <>
              <p>Подтверждать почту не нужно — но проверьте адрес: по нему восстанавливают доступ.</p>
              <p>Пароль — от 10 символов.</p>
              <label><input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} /> Я принимаю Условия использования и Политику конфиденциальности</label>
            </>
          ) : null}
          {err ? <p className="alert">{err}</p> : null}
          <button className="btn" type="submit">{mode === "login" ? "Войти по email" : "Создать"}</button>
        </form>
        <div className="row">
          {caps.magicLink ? <a href="/auth/magic">Получить ссылку для входа</a> : null}
          {caps.telegram && caps.telegramBotUsername ? (
            <TelegramLogin
              username={caps.telegramBotUsername}
              onAuth={async (payload) => {
                try {
                  await api("/api/v1/auth/telegram", { method: "POST", body: JSON.stringify(payload) });
                  nav(next);
                } catch (ex) {
                  setErr((ex as Error).message || "Не удалось войти через Telegram. Попробуйте снова или используйте email.");
                }
              }}
            />
          ) : null}
        </div>
        {mode === "login" ? <p><a href={`/register?next=${encodeURIComponent(next)}`}>Нет аккаунта? Создать</a> · <a href="/auth/reset">Не помню пароль</a></p> : <p><a href={`/login?next=${encodeURIComponent(next)}`}>У меня уже есть аккаунт</a></p>}
      </main>
    </PocketFrame>
  );
}

function Storefront() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api<Record<string, unknown>>("/api/v1/public/storefront").then(setData).catch((e) => setErr(e.message));
  }, []);
  const seller = data?.seller as { display_name: string; bio: string; specialization: string; demo: boolean; slug: string } | undefined;
  const listings = (data?.listings as Array<{ title: string; slug: string; goal: string; age_label: string; duration_label: string; lesson_count: number }>) ?? [];
  const prices = (data?.prices as PublicPrice[]) ?? [];
  const catalogPrices = prices.filter((p) => p.coverageKind === "catalog");
  return (
    <PocketFrame>
      <header className="topbar"><a href="https://clever.party1207.online" className="brand">Clever</a></header>
      <main className="page storefront">
        {err ? <p>{err}</p> : null}
        {seller?.demo ? <p className="badge">Демонстрационный профиль</p> : null}
        <h1>Тренажёры {seller?.display_name ?? "…"}</h1>
        <p className="lead">{seller?.bio}</p>
        <p className="muted">Специализация: {seller?.specialization}</p>
        <p className="muted">{seller?.display_name} — автор тренажёров. Живых уроков внутри Clever нет: занятие ведёт сам тренажёр.</p>
        <div className="store">
        <section className="card">
          <h2>Цены</h2>
          <p>Доступ берут разово или на месяц — к одному тренажёру или ко всему каталогу.</p>
          <ul>
            {prices.map((p) => (
              <li key={p.offerVersionId}>
                {p.coverageKind === "catalog" ? "Все тренажёры" : p.title} · {p.kind === "monthly" ? "каждый месяц" : "разово"}: {formatRub(p.priceKopecks)}
              </li>
            ))}
          </ul>
        </section>
        <div className="grid3">
          {listings.map((t) => (
            <article key={t.slug} className="card">
              <p className="tile-emoji" aria-hidden="true">{trainerEmoji(t.slug)}</p>
              <h2>{t.title}</h2>
              <p>Цель: {t.goal}</p>
              <p>Возраст: {t.age_label}. Одно занятие: {t.duration_label}. Программа: {t.lesson_count} занятий.</p>
              {prices.filter((p) => p.trainerSlug === t.slug).map((p) => (
                <p key={p.offerVersionId}>{p.kind === "monthly" ? "Каждый месяц" : "Разово"}: {formatRub(p.priceKopecks)}</p>
              ))}
              <a className="btn ghost" href={`/preview/${t.slug}`}>Посмотреть preview</a>
              <a className="btn" href={`/start/${t.slug}`}>Попробовать 7 дней</a>
            </article>
          ))}
        </div>
        </div>
        {catalogPrices.length ? (
          <section className="card">
            <h2>Весь каталог</h2>
            {catalogPrices.map((p) => (
              <p key={p.offerVersionId}>{p.kind === "monthly" ? "Каждый месяц" : "Разово"}: {formatRub(p.priceKopecks)}</p>
            ))}
          </section>
        ) : null}
        <div className="trust">
          <p>Цена и условия видны до начала занятий.</p>
          <p>Пробный доступ — семь дней.</p>
          <p>Прогресс остаётся и после пробного периода.</p>
          <p>Репетитор не видит имя ребёнка и его ответы.</p>
          <p>Записи голоса не уходят на сервер.</p>
          <p>Пока идёт beta, оплата тестовая: деньги не списываются.</p>
        </div>
        <footer>Сделано на Clever · <a href="https://clever.party1207.online/">Собрать свои тренажёры под своим именем</a></footer>
      </main>
    </PocketFrame>
  );
}

function AppHome() {
  const [me] = useMe();
  const [family, setFamily] = useState<{ learners: Array<{ id: string; nickname: string }>; hasPin: boolean } | null>(null);
  const [resume, setResume] = useState<{ lastItemIndex: number; total: number; trainerSlug: string } | null>(null);
  useEffect(() => {
    api<{ learners: Array<{ id: string; nickname: string }>; hasPin: boolean }>("/api/v1/family").then(setFamily).catch(() => setFamily({ learners: [], hasPin: false }));
    api<{ session: { lastItemIndex: number; total: number; trainerSlug: string } | null }>("/api/v1/learning/sessions/current?trainerSlug=english-sounds")
      .then((d) => setResume(d.session))
      .catch(() => undefined);
  }, []);
  if (me === undefined) return <PocketFrame><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to="/login" />;
  return (
    <PocketFrame>
      <div className="shell">
        <aside className="side">
          <div className="brand">Clever</div>
          <nav className="nav">
            <a href="/app">Сегодня</a>
            <a href="/app/trainers">Тренажёры</a>
            <a href="/app/progress">Прогресс</a>
            <a href="/family">Семья</a>
            <a href="/family/rewards">Награды</a>
            <a href="/seller">Репетитор</a>
            {me.roles.includes("admin") ? <a href="/admin">Админ</a> : null}
          </nav>
        </aside>
        <main className="main today">
          <h1>Привет{family?.learners[0] ? `, ${family.learners[0].nickname}` : ""}. Продолжим?</h1>
          <section className="continue">
            <p>Занятие на сегодня — примерно 8 минут.</p>
            {resume && resume.lastItemIndex > 0 ? (
              <>
                <a className="btn" href={`/app/play/${resume.trainerSlug}`}>Продолжить с места остановки</a>
                <p className="muted">Мы запомнили шаг, на котором ты остановился.</p>
              </>
            ) : (
              <>
                <a className="btn" href="/app/play/english-sounds">Начать занятие</a>
                <p className="muted">Если прервёшься, вернёмся на тот же шаг.</p>
              </>
            )}
          </section>
          <ul className="tiles">
            {TRAINER_META.map((t) => (
              <li key={t.slug}>
                <a href={`/app/play/${t.slug}`}>
                  <span aria-hidden="true">{trainerEmoji(t.slug)}</span>
                  <strong>{t.title}</strong>
                  <em>{t.goal}</em>
                </a>
              </li>
            ))}
          </ul>
        </main>
      </div>
      <nav className="bottom">
        <a href="/app">Сегодня</a>
        <a href="/app/trainers">Тренажёры</a>
        <a href="/app/progress">Прогресс</a>
        <a href="/family">Профиль</a>
      </nav>
    </PocketFrame>
  );
}


function ProgressPage() {
  const [items, setItems] = useState<Array<{ skill_id: string; status: MasteryStatus; score: number }>>([]);
  useEffect(() => {
    api<{ items: typeof items }>("/api/v1/learning/progress").then((d) => setItems(d.items)).catch(() => undefined);
  }, []);
  return (
    <PocketFrame>
    <div className="page">
      <h1>Карта навыков</h1>
      <table className="progress-table">
        <thead><tr><th>Навык</th><th>Статус</th><th>Опора</th></tr></thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.skill_id}><td>{s.skill_id}</td><td>{CHILD_STATUS[s.status]}</td><td>{Math.round(s.score)}</td></tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 ? <p>Прогресс появится после первого занятия.</p> : null}
    </div>
    </PocketFrame>
  );
}

function Legal({ kind }: { kind: "terms" | "privacy" }) {
  return (
    <PocketFrame>
    <div className="page">
      <h1>{kind === "terms" ? "Условия использования" : "Политика конфиденциальности"}</h1>
      <p>Репетитор не видит имя ребёнка, почту семьи, ответы, речь и награды. Запись микрофона остаётся только в текущем упражнении и только в этом браузере.</p>
      <p><a href="/credits">Credits и лицензии медиа</a></p>
    </div>
    </PocketFrame>
  );
}

function Pwa() {
  const loc = useLocation();
  const next = useMemo(() => safeNext(new URLSearchParams(loc.search).get("next")), [loc.search]);
  return (
    <PocketFrame>
    <div className="page onboard">
      <p className="brand">Clever</p>
      <h1>Добавить Clever на главный экран?</h1>
      <p>Тогда занятия открываются как обычное приложение — без адресной строки и поиска нужной вкладки.</p>
      <h2>iPhone/iPad, Safari</h2>
      <ol>
        <li>Откройте Clever в Safari.</li>
        <li>Нажмите “Поделиться” — квадрат со стрелкой вверх.</li>
        <li>Прокрутите список и выберите “На экран «Домой»”.</li>
        <li>Включите “Открыть как веб{"\u2011"}приложение”.</li>
        <li>Нажмите “Добавить”.</li>
      </ol>
      <p>Если пункта нет, выберите “Редактировать действия” и добавьте “На экран «Домой»”.</p>
      <h2>Android, Chrome</h2>
      <ol>
        <li>Откройте Clever в Chrome.</li>
        <li>Нажмите меню ⋮ справа от адресной строки.</li>
        <li>Выберите “Установить и создать ярлык”.</li>
        <li>Нажмите “Установить”.</li>
      </ol>
      <p>Если доступен только ярлык, выберите “Добавить на главный экран” → “Создать ярлык”.</p>
      <a className="btn" href={next}>Продолжить</a>
    </div>
    </PocketFrame>
  );
}

function GuestPreview() {
  const { slug } = useParams();
  const loc = useLocation();
  const tenant = tenantSlugFromHost();
  const seller = new URLSearchParams(loc.search).get("seller") || tenant || "anna-clever";
  useEffect(() => {
    if (tenant && slug) {
      window.location.replace(`${APEX}/preview/${slug}?seller=${encodeURIComponent(tenant)}`);
    }
  }, [slug, tenant]);
  if (tenant) return <PocketFrame><p className="page">Переходим на clever.party1207.online…</p></PocketFrame>;
  return <PlayPage extra={<a className="btn" href={`/start/${slug}?seller=${encodeURIComponent(seller)}`}>Попробовать 7 дней на clever.party1207.online</a>} />;
}

function StartGate() {
  const { slug } = useParams();
  const loc = useLocation();
  const [me] = useMe();
  const tenant = tenantSlugFromHost();
  const seller = new URLSearchParams(loc.search).get("seller") || tenant || "anna-clever";
  const dest = `/start/${slug}?seller=${encodeURIComponent(seller)}`;
  useEffect(() => {
    if (tenant && slug) {
      window.location.replace(`${APEX}${dest}`);
    }
  }, [tenant, slug, dest]);
  if (tenant) return <PocketFrame><p className="page">Пробный доступ открывается на clever.party1207.online…</p></PocketFrame>;
  if (me === undefined) return <PocketFrame><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(dest)}`} />;
  if (!me.activeLearnerId) return <Navigate to={`/family?next=${encodeURIComponent(dest)}`} />;
  return <PlayPage />;
}

function RecoveryCodes() {
  const nav = useNavigate();
  const loc = useLocation();
  const state = loc.state as { recoveryCodes?: string[]; next?: string } | null;
  const codes = state?.recoveryCodes ?? [];
  const next = safeNext(state?.next ?? null);
  const onboard = `/pwa?next=${encodeURIComponent(next)}`;
  return (
    <PocketFrame>
    <div className="page">
      <h1>Сохраните коды восстановления</h1>
      {codes.length ? (
        <>
          <p>Покажем их только сейчас. Каждый код срабатывает один раз.</p>
          <ol>
            {codes.map((code) => (
              <li key={code}><code>{code}</code></li>
            ))}
          </ol>
          <button className="btn" type="button" onClick={() => nav(onboard, { replace: true })}>Готово</button>
        </>
      ) : (
        <>
          <p>Коды уже были показаны. Их нельзя посмотреть снова.</p>
          <a className="btn" href={onboard}>Продолжить</a>
        </>
      )}
    </div>
    </PocketFrame>
  );
}

function ResetRequest() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  return (
    <PocketFrame>
    <div className="page">
      <h1>Восстановить доступ</h1>
      <p>Отправим одноразовую ссылку для создания нового пароля.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          try {
            await api("/api/v1/auth/password-reset/request", {
              method: "POST",
              body: JSON.stringify({ email }),
            });
            setMsg(`Если аккаунт с адресом ${email} существует, письмо со ссылкой уже отправлено.`);
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
        {err ? <p className="alert">{err}</p> : null}
        {msg ? <p>{msg}</p> : null}
        <button className="btn" type="submit">Отправить ссылку</button>
      </form>
      <p><a href="/login">Войти</a></p>
    </div>
    </PocketFrame>
  );
}

function ResetConfirm() {
  const loc = useLocation();
  const token = useMemo(() => new URLSearchParams(loc.search).get("token"), [loc.search]);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [revokeOther, setRevokeOther] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  if (!token) return <Navigate to="/auth/reset" replace />;
  return (
    <PocketFrame>
    <div className="page">
      <h1>Создайте новый пароль</h1>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          if (password !== passwordConfirm) {
            setErr("Пароли не совпадают.");
            return;
          }
          try {
            await api("/api/v1/auth/password-reset/confirm", {
              method: "POST",
              body: JSON.stringify({ token, password, passwordConfirm, revokeOtherSessions: revokeOther }),
            });
            setMsg("Пароль изменён. Теперь можно войти.");
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <label className="field">Новый пароль<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
        <label className="field">Повторите пароль<input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} autoComplete="new-password" /></label>
        <label>
          <input type="checkbox" checked={revokeOther} onChange={(e) => setRevokeOther(e.target.checked)} />
          Завершить другие сессии
        </label>
        <p className="muted">На других устройствах потребуется войти снова.</p>
        {err ? <p className="alert">{err}</p> : null}
        {msg ? <p>{msg}</p> : null}
        <button className="btn" type="submit">Создать новый пароль</button>
      </form>
      {msg ? <p><a href="/login">Войти в Clever</a></p> : <p><a href="/login">Войти</a></p>}
    </div>
    </PocketFrame>
  );
}

function ResetGate() {
  const loc = useLocation();
  const token = new URLSearchParams(loc.search).get("token");
  if (token) return <Navigate to={`/auth/reset/confirm?token=${encodeURIComponent(token)}`} replace />;
  return <ResetRequest />;
}

function PrivacyPage() {
  const [me] = useMe();
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  if (me === undefined) return <PocketFrame><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to="/login?next=/family/privacy" />;
  return (
    <PocketFrame>
    <div className="page">
      <h1>Конфиденциальность и удаление данных</h1>
      <p>Кто видит данные ребёнка</p>
      <p>Связанные взрослые видят прогресс, доступ и домашние награды.</p>
      <p>Репетитор видит только агрегированную статистику. Ему недоступны имя ребёнка, email семьи, отдельные ответы, история занятий, награды и голос.</p>
      <p>Временные записи голоса не отправляются на сервер.</p>
      <h2>Удаление аккаунта</h2>
      <p>После подтверждения пароля сессии завершатся. Семь дней можно отменить удаление.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          try {
            const res = await api<{ cancelUntil: string }>("/api/v1/privacy/deletion-requests", {
              method: "POST",
              body: JSON.stringify({ password }),
            });
            setMsg(`Удаление запрошено. Его можно отменить до ${new Date(res.cancelUntil).toLocaleString("ru-RU")}.`);
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <label className="field">Пароль<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></label>
        {err ? <p className="alert">{err}</p> : null}
        {msg ? <p>{msg}</p> : null}
        <button className="btn" type="submit">Удалить аккаунт</button>
      </form>
      <p><a href="/auth/deletion-cancel">Отменить удаление</a></p>
    </div>
    </PocketFrame>
  );
}

function DeletionCancel() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  return (
    <PocketFrame>
    <div className="page">
      <h1>Отменить удаление</h1>
      <p>Подтвердите вход паролем, чтобы отменить удаление в течение семи дней.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          try {
            await api("/api/v1/privacy/deletion-requests/cancel", {
              method: "POST",
              body: JSON.stringify({ email, password }),
            });
            setMsg("Удаление отменено. Теперь можно войти.");
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
        <label className="field">Пароль<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></label>
        {err ? <p className="alert">{err}</p> : null}
        {msg ? <p>{msg}</p> : null}
        <button className="btn" type="submit">Отменить удаление</button>
      </form>
      <p><a href="/login">Войти</a></p>
    </div>
    </PocketFrame>
  );
}

function LabGate() {
  return <LabApp />;
}

function HostSwitch() {
  const host = window.location.hostname;
  const isTenant = host.endsWith(".clever.party1207.online") && host !== "clever.party1207.online";
  if (isTenant) return <Storefront />;
  return <Landing />;
}

function Magic() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const loc = useLocation();
  const token = useMemo(() => new URLSearchParams(loc.search).get("token"), [loc.search]);
  if (token) {
    return (
      <PocketFrame>
      <div className="page">
        <h1>Войти по ссылке</h1>
        <button className="btn" type="button" onClick={() => api("/api/v1/auth/magic/confirm", { method: "POST", body: JSON.stringify({ token }) }).then(() => location.assign("/app"))}>Войти в Clever</button>
      </div>
      </PocketFrame>
    );
  }
  return (
    <PocketFrame>
    <div className="page">
      <h1>Войти по ссылке</h1>
      <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      <button className="btn" type="button" onClick={async () => {
        const r = await api<{ message: string }>("/api/v1/auth/magic/request", { method: "POST", body: JSON.stringify({ email }) });
        setMsg(r.message);
      }}>Отправить ссылку</button>
      <p>{msg}</p>
    </div>
    </PocketFrame>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HostSwitch />} />
        <Route path="/tutors" element={<Navigate to="/" replace />} />
        <Route path="/families" element={<Families />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/login" element={<AuthForm mode="login" />} />
        <Route path="/register" element={<AuthForm mode="register" />} />
        <Route path="/auth/magic" element={<Magic />} />
        <Route path="/auth/magic/confirm" element={<Magic />} />
        <Route path="/auth/reset" element={<ResetGate />} />
        <Route path="/auth/reset/confirm" element={<ResetConfirm />} />
        <Route path="/auth/recovery" element={<RecoveryCodes />} />
        <Route path="/auth/deletion-cancel" element={<DeletionCancel />} />
        <Route path="/family/privacy" element={<PrivacyPage />} />
        <Route path="/app" element={<AppHome />} />
        <Route path="/app/trainers" element={<AppHome />} />
        <Route path="/app/progress" element={<ProgressPage />} />
        <Route path="/app/play/:slug" element={<PlayPage />} />
        <Route path="/preview/:slug" element={<GuestPreview />} />
        <Route path="/start/:slug" element={<StartGate />} />
        <Route path="/family" element={<FamilyPage />} />
        <Route path="/family/rewards" element={<RewardsPage />} />
        <Route path="/family/paywall" element={<Paywall />} />
        <Route path="/seller/*" element={<SellerCabinet />} />
        <Route path="/design-lab/*" element={<LabGate />} />
        <Route path="/admin/design-lab/*" element={<LabGate />} />
        <Route path="/admin/totp" element={<TotpPage />} />
        <Route path="/admin/*" element={<AdminCabinet />} />
        <Route path="/pwa" element={<Pwa />} />
        <Route path="/legal/terms" element={<Legal kind="terms" />} />
        <Route path="/legal/privacy" element={<Legal kind="privacy" />} />
        <Route path="*" element={<PocketFrame><div className="page"><h1>Страница не найдена</h1></div></PocketFrame>} />
      </Routes>
    </BrowserRouter>
  );
}
